import React from 'react';
import axios, { AxiosInstance } from 'axios';
import { useUserContext } from './userContext';

export interface IHttpClientContext {
  coreApiHttpClient: AxiosInstance;
  authApiHttpClient: AxiosInstance;
  llmApiHttpClient: AxiosInstance;
  ingestionApiHttpClient: AxiosInstance;
}

const HttpClientContext = React.createContext<IHttpClientContext>({} as IHttpClientContext);

const HttpClientProvider = ({ children }: any) => {
  const userContext = useUserContext();

  const createHttpClient = (baseURL: string) => {
    const client = axios.create({
      baseURL,
    });

    client.interceptors.request.use(async (config) => {
      const accessToken = await userContext?.getAccessToken();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      const activeDomain =
        localStorage.getItem('activeDomain') ||
        userContext?.user?.domainId ||
        (() => {
          const hostParts = window.location.hostname.split('.');
          if (hostParts.length > 2 && hostParts[0] !== 'www' && hostParts[0] !== 'app') {
            return hostParts[0];
          }
          return undefined;
        })();
      if (activeDomain) {
        config.headers['x-domain-id'] = activeDomain;
      }
      return config;
    });

    client.interceptors.response.use(
      function (response) {
        return response;
      },
      function (error) {
        const { code, message, response = {} } = error;
        const { data = {}, status } = response;
        const { errorCode, message: errorMessage } = data;
        const fallBackMEssage = errorCode ? `API error code: ${errorCode}` : `Http error code: ${code}`;
        return Promise.reject({
          httpCode: code,
          message: errorMessage || message || fallBackMEssage,
          errorCode,
          status,
        });
      },
    );

    return client;
  };

  const coreApiHttpClient = createHttpClient(import.meta.env.VITE_CORE_API_BASE_URL);
  const authApiHttpClient = createHttpClient(import.meta.env.VITE_AUTH_API_BASE_URL);
  const llmApiHttpClient = createHttpClient(import.meta.env.VITE_LLM_API_BASE_URL);
  const ingestionApiHttpClient = createHttpClient(import.meta.env.VITE_INGESTION_API_BASE_URL);

  return (
    <HttpClientContext.Provider
      value={{ coreApiHttpClient, authApiHttpClient, llmApiHttpClient, ingestionApiHttpClient }}
    >
      {children}
    </HttpClientContext.Provider>
  );
};

function useHttpClientContext() {
  const context = React.useContext(HttpClientContext);
  if (context === undefined) {
    throw new Error('useHttpClientContext must be used within a HttpClientProvider');
  }
  return context;
}

export { HttpClientProvider, useHttpClientContext };
